// Pure quiz-scoring engine. No DOM, no state — deterministic and fully testable.
// Shapes match `services/src/content/quiz.schema.json`.
//
// SECURITY NOTE: in the current API contract the SPA computes the score and submits
// {quiz_id, score, total}; the backend validates 0 <= score <= total but trusts the
// number. Because this function is pure and portable, the hardening path is to submit
// the raw `answers` map and re-run scoreQuiz() server-side so a tampered client can't
// inflate a score. Kept here as the single source of truth for both sides.

export interface Question {
  id: number;
  q: string;
  opts: string[];
  ans: number[]; // indices of correct options
  exp?: string;
}
export interface Section {
  title: string;
  questions: Question[];
}
export interface Quiz {
  id: string;
  title: string;
  locked: boolean;
  sections: Section[];
}

/** questionId -> selected option indices (absent = unanswered) */
export type Answers = Record<number, number[]>;

export interface SectionScore {
  title: string;
  score: number;
  total: number;
  pct: number;
}
export interface QuizScore {
  score: number;
  total: number;
  percentage: number;
  sections: SectionScore[];
}

function sameSet(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const seen = new Set(a);
  return b.every((x) => seen.has(x));
}

/**
 * Score a full quiz. Multi-answer questions are all-or-nothing (the selected set
 * must equal the correct set). Unanswered questions count toward the total but are
 * wrong. The total is always the full quiz — a partial `answers` map (e.g. a
 * "retake failed only" pass) does not shrink the denominator.
 */
export function scoreQuiz(quiz: Quiz, answers: Answers): QuizScore {
  let score = 0;
  let total = 0;
  const sections = quiz.sections.map((section) => {
    let ss = 0;
    let st = 0;
    for (const question of section.questions) {
      st++;
      total++;
      const selected = answers[question.id] ?? [];
      if (sameSet(selected, question.ans)) {
        ss++;
        score++;
      }
    }
    return { title: section.title, score: ss, total: st, pct: st ? Math.round((ss / st) * 100) : 0 };
  });
  const percentage = total ? Math.round((score / total) * 1000) / 10 : 0;
  return { score, total, percentage, sections };
}
